import { useEffect, useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { when } from '../format.js';
import { texts, useLang } from '../i18n.js';
import { toast } from '../ui.js';

/**
 * "Väntar på godkännande" (owner 2026-10-28): requests for access that confirmed their address. Admins decide about
 * staff under People (employee or freelancer, with a role); whoever edits clients decides about clients under Clients by
 * tying them to a client, which makes a contact and sends the portal invitation. Nothing shows when nothing waits.
 */

export interface AccessView {
  id: string;
  kind: 'staff' | 'client';
  name: string;
  email: string;
  company: string | null;
  role: string | null;
  message: string | null;
  status: 'pending' | 'approved' | 'declined';
  at: string;
  decidedAt: string | null;
  decidedBy: string | null;
  suggest: { id: string; name: string } | null;
  client: { id: string; name: string } | null;
}

const TX = texts(
  {
    title: (n: number) => `Waiting for approval (${n})`,
    lead: 'They confirmed their address with a code. Nobody gets in until someone here approves.',
    says: 'Says they do:',
    roles: { editor: 'Editing', camera: 'Camera', producer: 'Producer', other: 'Something else', admin: 'Admin', cd: 'Creative Director', sales: 'Sales' } as Record<string, string>,
    from: (c: string) => `From ${c}`,
    employee: 'Employee',
    freelancer: 'Freelancer',
    role: 'Role',
    client: 'Client',
    pickClient: 'Choose client',
    suggested: (n: string) => `Suggested: ${n} (a colleague is a contact there)`,
    canApprove: 'Approves films',
    approve: 'Approve',
    decline: 'Reject',
    approvedStaff: (n: string, pw: boolean) => (pw ? `${n} is in the Hub and got "Choose your password"` : `${n} is in the Hub`),
    approvedClient: (n: string, mailed: boolean) => (mailed ? `${n} is a contact and got the portal invitation` : `${n} is a contact – copy the invitation from the client card`),
    declined: (n: string) => `${n} got a short no by email`,
    recent: 'Decided the last 30 days',
    approvedBy: (by: string | null, c: string | null) => `Approved${c ? ` · ${c}` : ''}${by ? ` · ${by}` : ''}`,
    declinedBy: (by: string | null) => `Rejected${by ? ` · ${by}` : ''}`
  },
  {
    title: (n: number) => `Väntar på godkännande (${n})`,
    lead: 'De har bekräftat sin adress med en kod. Ingen kommer in förrän någon här godkänner.',
    says: 'Säger att hen jobbar med:',
    roles: { editor: 'Redigering', camera: 'Kamera', producer: 'Producent', other: 'Annat', admin: 'Admin', cd: 'Creative Director', sales: 'Sälj' } as Record<string, string>,
    from: (c: string) => `Från ${c}`,
    employee: 'Anställd',
    freelancer: 'Frilansare',
    role: 'Roll',
    client: 'Kund',
    pickClient: 'Välj kund',
    suggested: (n: string) => `Förslag: ${n} (en kollega är kontakt där)`,
    canApprove: 'Godkänner filmer',
    approve: 'Godkänn',
    decline: 'Neka',
    approvedStaff: (n: string, pw: boolean) => (pw ? `${n} finns nu i Hubben och har fått "Välj ditt lösenord"` : `${n} finns nu i Hubben`),
    approvedClient: (n: string, mailed: boolean) => (mailed ? `${n} är kontakt och har fått inbjudan till portalen` : `${n} är kontakt – kopiera inbjudan från kundkortet`),
    declined: (n: string) => `${n} har fått ett kort nej på mejl`,
    recent: 'Beslutade de senaste 30 dagarna',
    approvedBy: (by: string | null, c: string | null) => `Godkänd${c ? ` · ${c}` : ''}${by ? ` · ${by}` : ''}`,
    declinedBy: (by: string | null) => `Nekad${by ? ` · ${by}` : ''}`
  }
);

const STAFF_ROLES = ['editor', 'camera', 'producer', 'cd', 'sales', 'admin'];
const FREELANCER_ROLES = ['editor', 'camera'];
const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function AccessRequests(p: { api: Api; kind: 'staff' | 'client'; clients?: { id: string; name: string }[]; onChanged?: () => void }) {
  const lang = useLang();
  const t = TX[lang];
  const [rows, setRows] = useState<AccessView[] | null>(null);
  const path = p.kind === 'staff' ? '/api/admin/access-requests' : '/api/clients/access-requests';
  const load = () =>
    p.api.get<{ requests: AccessView[] }>(path).then(
      (r) => setRows(r.requests),
      () => setRows([])
    );
  useEffect(() => {
    void load();
  }, [p.kind]);
  useEffect(() => {
    // the bell and the email link here with ?access=1
    if (rows?.some((r) => r.status === 'pending') && new URLSearchParams(location.search).has('access')) document.getElementById('access-h')?.scrollIntoView({ block: 'start' });
  }, [rows]);
  if (!rows) return null;
  const open = rows.filter((r) => r.status === 'pending');
  const done = rows.filter((r) => r.status !== 'pending');
  if (!open.length) return null;
  const changed = () => {
    void load();
    p.onChanged?.();
  };
  return (
    <section class="blk" aria-labelledby="access-h">
      <h2 class="blk-title" id="access-h">
        {t.title(open.length)}
      </h2>
      <p class="small muted">{t.lead}</p>
      <div class="access-list">
        {open.map((r) => (p.kind === 'staff' ? <StaffRow key={r.id} api={p.api} r={r} onDone={changed} /> : <ClientRow key={r.id} api={p.api} r={r} clients={p.clients ?? []} onDone={changed} />))}
      </div>
      {done.length ? (
        <details class="small">
          <summary>{t.recent}</summary>
          <ul>
            {done.map((r) => (
              <li>
                {r.name} ({r.email}) · {r.status === 'approved' ? t.approvedBy(r.decidedBy, r.client?.name ?? null) : t.declinedBy(r.decidedBy)}
                {r.decidedAt ? ` · ${when(r.decidedAt, lang)}` : ''}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </section>
  );
}

function Head(p: { r: AccessView }) {
  const lang = useLang();
  const t = TX[lang];
  const r = p.r;
  return (
    <>
      <div class="access-row-head">
        <strong>{r.name}</strong>
        <span class="small">{r.email}</span>
        <span class="small muted">{when(r.at, lang)}</span>
      </div>
      {r.kind === 'staff' && r.role ? (
        <span class="small">
          {t.says} {t.roles[r.role] ?? r.role}
        </span>
      ) : null}
      {r.company ? <span class="small">{t.from(r.company)}</span> : null}
      {r.message ? <q class="small">{r.message}</q> : null}
    </>
  );
}

function useDecide(api: Api, onDone: () => void) {
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<string>) => {
    setBusy(true);
    try {
      toast({ text: await fn() });
      onDone();
    } catch (e) {
      toast({ text: errText(e) });
    } finally {
      setBusy(false);
    }
  };
  return { busy, run, api };
}

function StaffRow(p: { api: Api; r: AccessView; onDone: () => void }) {
  const t = TX[useLang()];
  const r = p.r;
  const guess = r.role === 'editor' || r.role === 'camera' || r.role === 'producer' ? r.role : 'editor';
  const [kind, setKind] = useState<'employee' | 'freelancer'>(r.email.endsWith('@klarafardigafilm.se') ? 'employee' : 'freelancer');
  const [role, setRole] = useState(guess);
  const allowed = kind === 'freelancer' ? FREELANCER_ROLES : STAFF_ROLES;
  const pick = allowed.includes(role) ? role : allowed[0]!;
  const d = useDecide(p.api, p.onDone);
  return (
    <div class="access-row">
      <Head r={r} />
      <span class="cluster">
        <select class="input" aria-label={t.employee} value={kind} onChange={(e) => setKind((e.target as HTMLSelectElement).value as 'employee' | 'freelancer')}>
          <option value="freelancer">{t.freelancer}</option>
          <option value="employee">{t.employee}</option>
        </select>
        <select class="input" aria-label={t.role} value={pick} onChange={(e) => setRole((e.target as HTMLSelectElement).value)}>
          {allowed.map((x) => (
            <option value={x}>{t.roles[x]}</option>
          ))}
        </select>
        <button
          type="button"
          class="btn btn-primary btn-sm"
          disabled={d.busy}
          onClick={() => void d.run(async () => t.approvedStaff(r.name, (await p.api.post<{ password: boolean }>(`/api/admin/access-requests/${r.id}/approve`, { kind, roles: [pick] })).password))}
        >
          {t.approve}
        </button>
        <button type="button" class="btn btn-sm" disabled={d.busy} onClick={() => void d.run(async () => (await p.api.post(`/api/admin/access-requests/${r.id}/decline`, {}), t.declined(r.name)))}>
          {t.decline}
        </button>
      </span>
    </div>
  );
}

function ClientRow(p: { api: Api; r: AccessView; clients: { id: string; name: string }[]; onDone: () => void }) {
  const t = TX[useLang()];
  const r = p.r;
  const [clientId, setClientId] = useState(r.suggest?.id ?? '');
  const [canApprove, setCanApprove] = useState(false);
  const d = useDecide(p.api, p.onDone);
  const sorted = [...p.clients].sort((a, b) => a.name.localeCompare(b.name, 'sv'));
  return (
    <div class="access-row">
      <Head r={r} />
      {r.suggest ? <span class="small process-ok">✓ {t.suggested(r.suggest.name)}</span> : null}
      <span class="cluster">
        <select class="input" aria-label={t.client} value={clientId} onChange={(e) => setClientId((e.target as HTMLSelectElement).value)}>
          <option value="">{t.pickClient}</option>
          {sorted.map((c) => (
            <option value={c.id}>{c.name}</option>
          ))}
        </select>
        <label class="small">
          <input type="checkbox" checked={canApprove} onChange={(e) => setCanApprove((e.target as HTMLInputElement).checked)} /> {t.canApprove}
        </label>
        <button
          type="button"
          class="btn btn-primary btn-sm"
          disabled={d.busy || !clientId}
          onClick={() => void d.run(async () => t.approvedClient(r.name, (await p.api.post<{ invite: { emailed: boolean } }>(`/api/clients/access-requests/${r.id}/approve`, { clientId, canApprove })).invite.emailed))}
        >
          {t.approve}
        </button>
        <button type="button" class="btn btn-sm" disabled={d.busy} onClick={() => void d.run(async () => (await p.api.post(`/api/clients/access-requests/${r.id}/decline`, {}), t.declined(r.name)))}>
          {t.decline}
        </button>
      </span>
    </div>
  );
}
